import React, { useEffect, useState, useMemo } from 'react';
import { Box, IconButton, Popover, Typography, styled, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../../../Components/CustomIcon';
import { PrimaryFormActionButton } from '../../../Components/StyledComponent';
import SignalsSelectionSearch from '../Tabs/BatteryVitals/SignalsSelectionSearch';


const MAX_METRICS = 4;

const popoverPaperStyles = {
    p: '16px',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    boxShadow: '2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)'
}

const HeaderBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const FooterBox = styled(Box)({
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-end',
});


const RowActionButton = styled(IconButton)(({
    active,
    defaultColor,
    isClickable,
    allowHover,
    shouldHighlight }) => ({
        width: 20,
        height: 20,
        borderRadius: '50px',
        pointerEvents: isClickable ? 'auto' : 'none',
        cursor: isClickable ? 'pointer' : 'default',
        '& .MuiSvgIcon-root': {
            color: shouldHighlight ? '#6D72F6' : defaultColor,
            fontSize: '20px',
        },
        '&:hover': {
            '& .MuiSvgIcon-root': {
                color: allowHover ? '#4D44D8' : defaultColor,
            },
        },
    }));

const ItemRow = ({ gridView, item, initialItems, onClick }) => {
    const { t } = useTranslation('common');

    const isSelected = useMemo(
        () => initialItems.some((m) => m.label === item.label),
        [initialItems, item.label]
    );
    const isFull = initialItems.length >= MAX_METRICS;
    const canModify = isSelected || !isFull;
    const iconName = isSelected ? 'minus-circle-icon' : 'add-circle-icon';
    const shouldHighlight = isSelected ? false : canModify;


    return (
        <Grid2 container alignItems="center" sx={{ py: 0.5 }}>
            <Grid2 xs={6} sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '202px' }}>
                <RowActionButton
                    allowHover={canModify}
                    isClickable={canModify}
                    active={canModify}
                    shouldHighlight={shouldHighlight}
                    defaultColor={canModify ? '#686868' : '#A2A2A2'}
                    onClick={() => onClick(item)}
                >
                    <CustomIcon name={iconName} />
                </RowActionButton>
                <Typography variant={canModify ? "fw400s14lh24c3D4B5A" : "fw400s14lh24cA2A2A2"}>{t(item.label)}</Typography>
            </Grid2>
            {gridView === 'double' && <Grid2 xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Typography variant={canModify ? "fw400s14lh24c818181" : "fw400s14lh24cA2A2A2"}>{t(item.value)}</Typography>
            </Grid2>}
        </Grid2>
    );
};

export default function VitalsSelectionDropdown({
    itemList = [],
    selectedItems,
    applySelectedItems,
    gridView = 'single',
    headerLabel = 'Select Metrics'
}) {
    const { t } = useTranslation(['common', 'generic_table']);
    const [anchorEl, setAnchorEl] = useState(null);
    const [initialItems, setInitialItems] = useState(selectedItems);
    const [searchTerm, setSearchTerm] = useState('');

    const open = Boolean(anchorEl);
    const id = open ? 'digital-twin-info-matrix-popover' : undefined;

    useEffect(() => {
        setInitialItems(selectedItems);
    }, [selectedItems]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRowClick = (metric) => {
        let updated = [...initialItems];

        const isSelected = initialItems.some((m) => m.label === metric.label);
        if (isSelected) {
            updated = updated.filter((m) => m.label !== metric.label);
        } else {
            if (updated.length >= MAX_METRICS) {
                updated.shift();
            }
            updated.push({ ...metric, selected: true });
        }

        setInitialItems(updated);
    };

    const sortedItemList = useMemo(() => {
        return [...itemList].sort((a, b) => {
            const aIndex = initialItems.findIndex((m) => m.label === a.label);
            const bIndex = initialItems.findIndex((m) => m.label === b.label);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [itemList, initialItems]);

    // Filter items whenever the search term changes
    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return sortedItemList;
        return sortedItemList.filter(item =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, sortedItemList]);

    return (
        <Box>
            <IconButton sx={{
                p: 0,
                background: '#F9F9F9',
                borderRadius: '6px',
                padding: '5px 5px',
                '&:hover svg': { color: '#6D72F6' }

            }} onClick={handleClick}>
                <CustomIcon name="primary-arrow-icon" rotate={90} />
            </IconButton>


            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{ paper: { sx: popoverPaperStyles } }}
            >
                <HeaderBox>
                    <Typography variant="fw700s14c3D4B5A" sx={{ lineHeight: '18px' }}>{t(headerLabel, headerLabel)}</Typography>
                </HeaderBox>

                <Box sx={{ py: 0.5, minWidth: '325px' }}>
                    <Typography variant="fw500s10lh24c5E758D" sx={{ lineHeight: '24px', color: "#87939D", mb: 0.5, display: 'block' }}>{t('Add upto 4 metrics')}</Typography>
                    <SignalsSelectionSearch value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                    <Grid2 container alignItems="center" sx={{ py: 0.5, mt: 1 }}>
                        <Grid2 xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '202px' }}>
                            <Typography variant="fw500s10c949494lh14" sx={{ color: '#8D8D8D' }}>{t('METRIC')}</Typography>
                        </Grid2>
                        {gridView === 'double' && <Grid2 xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography variant="fw500s10c949494lh14" sx={{ color: '#8D8D8D' }}>{t('STATUS')}</Typography>
                        </Grid2>}
                    </Grid2>

                    <Box sx={{ maxHeight: '225px', overflowY: 'auto' }}>
                        {filteredItems.map((item, index) => (
                            <ItemRow
                                key={index}
                                item={item}
                                initialItems={initialItems}
                                onClick={handleRowClick}
                                gridView={gridView}
                            />
                        ))}
                    </Box>
                </Box>

                <FooterBox>
                    <PrimaryFormActionButton
                        onClick={() => {
                            applySelectedItems(initialItems);
                            handleClose();
                        }}
                    >
                        {t('actions.apply')}
                    </PrimaryFormActionButton>
                </FooterBox>
            </Popover>
        </Box>
    );
}
